import * as _ from "lodash-es";
import { CSSProperties, useState, useContext, useEffect, useRef, useLayoutEffect } from "react";
import "./OwDoubleScrollbar.scss";
import UiContext from "../../../contexts/UiContext";

type Props = {
    /**
     * Element class to do lookup on for the total scroll width necessary.
     */
    childElemClass?: string;
    /**
     * Element Class to do lookup on for the container view width
     */
    viewElemClass?: string;
    /**
     * If the data needs to load, then we need to recalculate the width(s), this flag allows that to happen
     */
    isElemDataLoading?: boolean;
    /**
     * If the top scrollbar should be pinned to the bottom of the header.
     */
    pinToHeader?: boolean;
    /**
     * React 18 fix
     */
    children?: any;
};

const OwDoubleScrollbar = (props: Props) => {
    const outerDiv = useRef<HTMLDivElement | null>(null);
    const innerDiv = useRef<HTMLDivElement | null>(null);
    const childWrapper = useRef<HTMLDivElement | null>(null);
    const headerStandard = useRef<HTMLDivElement | null>(null);
    const uiContext = useContext(UiContext);

    const [width, setWidth] = useState("auto");
    const [mainViewWidth, setMainViewWidth] = useState("");
    const [mainViewOffsetLeft, setMainViewOffsetLeft] = useState("");
    const [stickyHeader, setStickyHeader] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);

    // Use a ref to track if component has mounted
    const hasMounted = useRef(false);

    // First mount effect
    useEffect(() => {
        hasMounted.current = true;

        const mainViewElem = props.viewElemClass ? document.getElementById(props.viewElemClass) : document.getElementById("mainView");

        if (mainViewElem) {
            mainViewElem.onscroll = onScroll;
        }

        window.addEventListener("resize", calculateWidth);

        // Immediate width calculation
        calculateWidth();

        // Additional calculation after a brief delay to ensure DOM is ready
        const timer = setTimeout(() => {
            calculateWidth();
            setIsInitialized(true);
        }, 100);

        return () => {
            window.removeEventListener("resize", calculateWidth);
            clearTimeout(timer);

            if (mainViewElem) {
                mainViewElem.onscroll = null;
            }
        };
    }, []);

    // Effect to watch for UI context changes (menu state)
    useEffect(() => {
        if (hasMounted.current) {
            calculateWidth();
        }
    }, [uiContext.menuIconMode, uiContext.menuOpen]);

    // Effect to watch for data loading changes
    useEffect(() => {
        if (hasMounted.current && props.isElemDataLoading !== undefined) {
            const timer = setTimeout(() => {
                calculateWidth();
            }, 50);

            return () => clearTimeout(timer);
        }
    }, [props.isElemDataLoading]);

    // Setup scroll sync between the two scrollbars
    useEffect(() => {
        if (childWrapper.current && outerDiv.current) {
            outerDiv.current.onscroll = () => {
                if (childWrapper.current && outerDiv.current) {
                    childWrapper.current.scrollLeft = outerDiv.current.scrollLeft;
                }
            };

            childWrapper.current.onscroll = () => {
                if (childWrapper.current && outerDiv.current) {
                    outerDiv.current.scrollLeft = childWrapper.current.scrollLeft;
                }
            };
        }
    }, [childWrapper, outerDiv, isInitialized]);

    // Force recalculation after component has rendered with children
    useLayoutEffect(() => {
        if (childWrapper.current && hasMounted.current) {
            calculateWidth();
        }
    }, [props.children]);

    const onScroll = () => {
        if (headerStandard && childWrapper) {
            let _stickyHeader = stickyHeader;

            const childDivRect = childWrapper.current?.getBoundingClientRect();

            if (headerStandard.current && childDivRect && headerStandard.current.offsetHeight >= childDivRect.top) {
                _stickyHeader = true;
            } else {
                _stickyHeader = false;
            }

            if (stickyHeader !== _stickyHeader) {
                setStickyHeader(_stickyHeader);
            }
        }
    };

    const getLookupSize = (isMenuOpen?: boolean, isMenuIconMode?: boolean) => {
        let lookupWidth = "100%";
        let lookupLeft = "15px";

        if (_.isNil(isMenuOpen)) {
            isMenuOpen = uiContext.menuOpen;
        }

        if (_.isNil(isMenuIconMode)) {
            isMenuIconMode = uiContext.menuIconMode;
        }

        const mainView = document.getElementById("mainView");

        if (mainView && isMenuOpen) {
            const padding = 15;
            lookupWidth = `${mainView.offsetWidth}px`;
            lookupLeft = `${mainView.offsetLeft + padding}px`;
        }

        return { lookupWidth, lookupLeft };
    };

    const calculateWidth = () => {
        let _width = getChildWrapperWidth();
        let _mainViewWidth = "";
        let _mainViewOffsetLeft = "";

        if (width === null) {
            _width = "auto";
        }

        const mainViewElem = props.viewElemClass ? document.getElementById(props.viewElemClass) : document.getElementById("mainView");

        if (mainViewElem) {
            _mainViewWidth = mainViewElem.offsetWidth + "px";

            const { lookupLeft } = getLookupSize(uiContext.menuOpen, uiContext.menuIconMode);
            _mainViewOffsetLeft = lookupLeft + "px";
        }

        if (width !== _width || mainViewWidth !== _mainViewWidth || mainViewOffsetLeft !== _mainViewOffsetLeft) {
            if (_width) {
                setWidth(_width);
            }
            setMainViewWidth(_mainViewWidth);
            setMainViewOffsetLeft(_mainViewOffsetLeft);
        }
    };

    const getChildWrapperWidth = () => {
        let width: string | null = null;

        if (!props.childElemClass) {
            if (childWrapper.current && childWrapper.current.scrollWidth) {
                width = childWrapper.current.scrollWidth + "px";
            }
        } else {
            const childWrapper = document.getElementsByClassName(props.childElemClass);

            if (childWrapper && childWrapper.length) {
                const firstElem = childWrapper[0];
                width = firstElem.scrollWidth + "px";
            }
        }

        return width;
    };

    const outerDivStyle: CSSProperties = {
        overflowX: "auto",
        overflowY: "hidden",
        width: mainViewWidth,
        left: mainViewOffsetLeft,
    };
    const innerDivStyle: CSSProperties = { paddingTop: "1px", width: width };
    const childDivStyle: CSSProperties = { overflow: "auto", overflowY: "hidden" };

    return (
        <div className={"OwDoubleScrollbar"}>
            <div ref={outerDiv} className={stickyHeader ? "sticky-scroll" : ""} style={outerDivStyle}>
                <div ref={innerDiv} style={innerDivStyle}>
                    {stickyHeader ? <>&nbsp;</> : null}
                </div>
            </div>
            <div ref={childWrapper} style={childDivStyle}>
                {props.children}
            </div>
        </div>
    );
};

export default OwDoubleScrollbar;
